import { create } from 'zustand';
import { projectsApi } from '../lib/api';

/**
 * Store Zustand per gestire lo stato del progetto corrente
 * Integrato con backend API
 */
const useProjectStore = create((set, get) => ({
  // Stato
  currentProject: null,
  projects: [],
  loading: false,
  error: null,

  // Actions

  /**
   * Carica tutti i progetti
   */
  loadProjects: async () => {
    set({ loading: true, error: null });
    try {
      const response = await projectsApi.getAll();
      // Backend restituisce { success: true, data: [...], pagination: {...} }
      // oppure se non paginato: { success: true, data: [...] }
      const projects = Array.isArray(response.data) ? response.data : (response.data.data || []);
      set({ projects, loading: false });
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      set({ error: errorMessage, loading: false });
    }
  },

  /**
   * Carica un progetto specifico
   */
  loadProject: async (projectId) => {
    set({ loading: true, error: null });
    try {
      const response = await projectsApi.getOne(projectId);
      const project = response.data;
      set({ currentProject: project, loading: false });
      return project;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  /**
   * Crea un nuovo progetto
   */
  createProject: async (projectData) => {
    set({ loading: true, error: null });
    try {
      const response = await projectsApi.create(projectData);
      const newProject = response.data;

      set((state) => ({
        currentProject: newProject,
        projects: [newProject, ...state.projects],
        loading: false
      }));

      return newProject;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  /**
   * Aggiorna un progetto
   */
  updateProject: async (projectId, updates) => {
    set({ loading: true, error: null });
    try {
      const response = await projectsApi.update(projectId, updates);
      const updatedProject = response.data;

      set((state) => ({
        currentProject: state.currentProject?._id === projectId ? updatedProject : state.currentProject,
        projects: state.projects.map(p => p._id === projectId ? updatedProject : p),
        loading: false
      }));

      return updatedProject;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  /**
   * Elimina un progetto (soft delete)
   */
  deleteProject: async (projectId) => {
    set({ loading: true, error: null });
    try {
      await projectsApi.delete(projectId);

      set((state) => ({
        currentProject: state.currentProject?._id === projectId ? null : state.currentProject,
        projects: state.projects.filter(p => p._id !== projectId),
        loading: false
      }));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  /**
   * Aggiorna stato progetto (pause, resume, archive)
   */
  updateStatus: async (projectId, status, reason = null) => {
    set({ loading: true, error: null });
    try {
      const response = await projectsApi.updateStatus(projectId, status, reason);
      const updatedProject = response.data;

      set((state) => ({
        currentProject: state.currentProject?._id === projectId ? updatedProject : state.currentProject,
        projects: state.projects.map(p => p._id === projectId ? updatedProject : p),
        loading: false
      }));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  /**
   * Reset del progetto corrente
   */
  clearCurrentProject: () => {
    set({ currentProject: null });
  },

  /**
   * Clear errori
   */
  clearError: () => set({ error: null })
}));

export default useProjectStore;
